import { Button, IconButton } from "@mui/material";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import CloseIcon from "@mui/icons-material/Close";

interface NotistackOptions {
	duration?: number;
	persist?: boolean;
	actions?: Record<string, (snackbar: { id: string | number; close(): void }) => void>;
}

export class NotistackServices {
	/**
	 * Exibe uma mensagem utilizando o Notistack.
	 *
	 * @param variant O tipo da mensagem: "success", "error", "info" ou "warning".
	 * @param message O conteúdo da mensagem a ser exibida.
	 * @param options Opções adicionais, como duração e ações personalizadas.
	 * @returns void
	 * @example
	 * ```ts
	 * NotistackServices.showMessage("success", "Operação realizada com sucesso!", { duration: 3000, actions: {
	 *   "Undo": ({ id, close }) => {
	 *     // Lógica para desfazer a ação
	 *     close();
	 *   },
	 *   "Dismiss": ({ id, close }) => {
	 *     close();
	 *   }
	 * } });
	 * ```
	 */
	static showMessage(variant: "success" | "error" | "info" | "warning", message: string, options: NotistackOptions = {}) {
		const { duration, persist, actions = {} } = options;
		enqueueSnackbar(message, {
			variant,
			autoHideDuration: duration,
			persist,
			action(snackbarId) {
				return (
					<>
						{Object.entries(actions).map(([key, actionFn], index) => (
							<Button
								color="inherit"
								size="small"
								key={index}
								onClick={() => {
									actionFn({
										id: snackbarId,
										close() {
											closeSnackbar(snackbarId);
										},
									});
								}}
							>
								{key}
							</Button>
						))}
						{Object.keys(actions).length <= 0 && (
							<IconButton
								aria-label="delete"
								size="small"
								onClick={() => closeSnackbar(snackbarId)}
							>
								<CloseIcon />
							</IconButton>
						)}
					</>
				);
			},
		});
	}

	/**
	 * Exibe uma mensagem de sucesso.
	 * @param message O conteúdo da mensagem a ser exibida.
	 * @param options Opções adicionais, como duração e ações personalizadas.
	 * @returns void
	 * @example
	 * ```ts
	 * NotistackServices.showSuccessMessage("Operação realizada com sucesso!", { duration: 3000 });
	 * ```
	 */
	static showSuccessMessage(message: string, options: NotistackOptions = {}) {
		this.showMessage("success", message, options);
	}

	/**
	 * Exibe uma mensagem de erro.
	 * @param message O conteúdo da mensagem a ser exibida.
	 * @param options Opções adicionais, como duração e ações personalizadas.
	 * @returns void
	 * @example
	 * ```ts
	 * NotistackServices.showErrorMessage("Ocorreu um erro ao processar sua solicitação.", { duration: 5000 });
	 * ```
	 */
	static showErrorMessage(message: string, options: NotistackOptions = {}) {
		this.showMessage("error", message, options);
	}

	/**
	 * Exibe uma mensagem informativa.
	 * @param message O conteúdo da mensagem a ser exibida.
	 * @param options Opções adicionais, como duração e ações personalizadas.
	 * @return void
	 * @example
	 * ```ts
	 * NotistackServices.showInfoMessage("Esta é uma mensagem informativa.", { duration: 4000 });
	 * ```
	 */
	static showInfoMessage(message: string, options: NotistackOptions = {}) {
		this.showMessage("info", message, options);
	}

	/**
	 * Exibe uma mensagem de aviso.
	 * @param message O conteúdo da mensagem a ser exibida.
	 * @param options Opções adicionais, como duração e ações personalizadas.
	 * @return void
	 * @example
	 * ```ts
	 * NotistackServices.showWarningMessage("Atenção! Verifique os dados inseridos.", { duration: 4000 });
	 * ```
	 */
	static showWarningMessage(message: string, options: NotistackOptions = {}) {
		this.showMessage("warning", message, options);
	}
}
